import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, Briefcase, Settings, LogOut } from "lucide-react";
import logo from "/img/logo.png"; // replace path as needed
import secureLocalStorage from "react-secure-storage";

export default function Topbar({
  logoColor = "#022D9C", // matching color from CBIU logo background
  user = { name: secureLocalStorage.getItem("name"), avatarUrl: secureLocalStorage.getItem("picture") || null },
  centerMenu = [{label:"Available Jobs",link:"/dashboard"}],
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const userRef = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserOpen(false);
      }
    }
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  return (
    <header
      className="w-full shadow-md"
      style={{ background: `linear-gradient(135deg, ${logoColor} 0%, ${logoColor}cc 100%)` }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
        <div className="relative flex items-center justify-between h-16">
          {/* Left: Welcome + Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen((s) => !s)}
              className="p-2 rounded-md md:hidden text-white hover:bg-white/10"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            <div className="flex items-center gap-3">
              <img src={logo} alt="CBIU Logo" className="w-10 h-10 rounded-md" />

              <div className="hidden sm:block">
                <div className="text-sm font-semibold">Welcome</div>
                <div className="text-xs opacity-90">{user.name}</div>
              </div>
            </div>
          </div>

          {/* Center: Menu (hidden on mobile) */}
          <nav className="hidden md:flex md:space-x-6">
            {centerMenu.map((m,key) => (
              <a
                key={key}
                href={m.link}
                className="relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-white/10 transition"
              >
                <span className="mr-2">{m === "Available Jobs" && <Briefcase size={16} />}</span>
                {m.label}
              </a>
            ))}
          </nav>

          {/* Right: User avatar */}
          <div className="relative" ref={userRef}>
            <button
              onClick={() => setUserOpen((s) => !s)}
              className="flex items-center gap-2 rounded-full p-1 focus:outline-none text-white hover:bg-white/10"
              aria-haspopup="true"
              aria-expanded={userOpen}
            >
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium">
                {user.avatarUrl ? (
                  <img src={`${import.meta.env.VITE_API_SOURCE_URL}${user.avatarUrl}`} alt="avatar" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <User size={16} />
                )}
              </div>
            </button>

            <AnimatePresence>
              {userOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-40 overflow-hidden text-slate-700"
                >
                  <a href="/dashboard/update-profile" className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                    <User size={16} /> <span className="text-sm">Update Profile</span>
                  </a>
                  <a href="/dashboard/settings" className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                    <Settings size={16} /> <span className="text-sm">Basic Setting</span>
                  </a>
                  <div className="border-t" />
                  <a href="/logout" className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-red-600">
                    <LogOut size={16} /> <span className="text-sm">Logout</span>
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile slide-down menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="md:hidden bg-white text-slate-700 shadow-lg"
          >
            <div className="px-4 pt-2 pb-4 space-y-2">
              {centerMenu.map((m,key) => (
                <a key={key} href={m.link} className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-50">
                  <Briefcase size={16} /> <span>{m.label}</span>
                </a>
              ))}

              <div className="border-t pt-2">
                <a href="/dashboard/update-profile" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-50">
                  <User size={16} /> Update Profile
                </a>
                <a href="/dashboard/settings" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-50">
                  <Settings size={16} /> Basic Setting
                </a>
                <a href="/logout" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-50 text-red-600">
                  <LogOut size={16} /> Logout
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}